import { useState } from "react";
import { type AuthProviders, api } from "./api.ts";
import { messages } from "./i18n.ts";

/**
 * A failed sign-in comes back as `/?error=<code>`. Without surfacing it the
 * screen just returns to the sign-in form having apparently done nothing, which
 * is indistinguishable from the button not working.
 */
const SIGN_IN_ERRORS: Record<string, string> = {
  unknown_user: messages.signIn.unknownUser,
  no_membership: messages.signIn.noMembership,
  email_unverified: messages.signIn.emailUnverified,
  session_expired: messages.signIn.sessionExpired,
  sign_in_failed: messages.signIn.failed,
  identity_provider_unreachable: messages.signIn.providerUnavailable,
  google_not_configured: messages.signIn.googleUnavailable,
};

function readSignInError(): string | null {
  const code = new URLSearchParams(window.location.search).get("error");
  if (!code) {
    return null;
  }
  // Clear it, so a reload does not keep showing a stale failure.
  window.history.replaceState({}, "", window.location.pathname);
  return SIGN_IN_ERRORS[code] ?? messages.signIn.failed;
}

/**
 * `providers` is null when the provider list could not be fetched, which is not
 * the same thing as an empty one and must not be rendered as one.
 *
 * The list is fetched from an endpoint behind the rate limiter, so a refusal is
 * an ordinary and transient outcome: a burst of traffic from one address, or a
 * reload during one. Reading that as "Google sign-in is not configured" states a
 * configuration fact that was never observed, and leaves somebody who is simply
 * early looking at a permanent-sounding fault with nothing to click.
 */
export function SignIn({
  providers,
  onRetryProviders,
  onSignedIn,
}: {
  providers: AuthProviders | null;
  onRetryProviders: () => void;
  onSignedIn: () => void;
}) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(readSignInError);
  const [busy, setBusy] = useState(false);

  const devSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.devSignIn(email);
      onSignedIn();
    } catch (caught) {
      setError(
        caught instanceof Error && caught.message === "unknown_user"
          ? messages.signIn.unknownUserLocal
          : messages.signIn.couldNotSignIn,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto flex min-h-dvh min-w-0 max-w-md flex-col justify-center gap-8 p-4 sm:p-8">
      <header className="flex flex-col gap-2">
        <p className="text-sm font-semibold uppercase tracking-widest text-orange">
          {messages.common.brand}
        </p>
        <h1 className="text-3xl font-semibold text-foreground">{messages.signIn.title}</h1>
        <p className="text-muted-foreground">{messages.signIn.intro}</p>
      </header>

      <section className="flex min-w-0 flex-col gap-4 rounded-lg border border-border bg-card p-5 shadow-sm sm:p-6">
        {providers === null ? (
          <>
            <p role="status" className="text-sm text-muted-foreground">
              {messages.signIn.optionsUnavailable}
            </p>
            <button
              className="rounded-md border border-border px-4 py-2 font-medium text-foreground"
              type="button"
              onClick={onRetryProviders}
            >
              {messages.signIn.retryOptions}
            </button>
          </>
        ) : (
          <>
            {providers.google ? (
              <a
                className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2.5 font-medium text-primary-foreground transition-opacity hover:opacity-90"
                href="/api/auth/google/start"
              >
                {messages.signIn.continueGoogle}
              </a>
            ) : (
              <p className="text-sm text-muted-foreground">{messages.signIn.googleUnavailable}</p>
            )}

            {providers.devLogin ? (
              <>
                <p className="text-center text-xs uppercase tracking-wider text-muted-foreground">
                  {providers.environment === "preview"
                    ? messages.signIn.previewDivider
                    : messages.signIn.localDivider}
                </p>
                {providers.environment === "preview" ? (
                  <p className="text-sm text-muted-foreground">{messages.signIn.previewAccounts}</p>
                ) : null}
                <form className="flex flex-col gap-3" onSubmit={devSignIn}>
                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="font-medium text-foreground">{messages.signIn.email}</span>
                    <input
                      className="rounded-md border border-input bg-background px-3 py-2 text-foreground"
                      type="email"
                      required
                      value={email}
                      placeholder={
                        providers.environment === "preview"
                          ? "reviewer@example.invalid"
                          : messages.signIn.emailPlaceholder
                      }
                      onChange={(event) => setEmail(event.target.value)}
                    />
                  </label>
                  <button
                    className="rounded-md border border-border px-4 py-2 font-medium text-foreground disabled:opacity-50"
                    type="submit"
                    disabled={busy}
                  >
                    {busy ? messages.signIn.signingIn : messages.signIn.localSignIn}
                  </button>
                </form>
              </>
            ) : null}
          </>
        )}

        {error ? (
          <p
            role="alert"
            className="rounded-md bg-status-overdue-bg px-3 py-2 text-sm text-status-overdue-fg"
          >
            {error}
          </p>
        ) : null}
      </section>
    </main>
  );
}
